import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, TimePicker, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { attendanceApi, show422Error, ValidationError } from '../../../utils/api';

const { TextArea } = Input;

const AttendanceAdjustModal = ({
  open,
  onClose,
  attendanceId,
  empName,
  jalaliDate,
  initialClockIn,
  initialClockOut,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      clock_in_time: initialClockIn
        ? dayjs(String(initialClockIn).slice(0, 5), 'HH:mm')
        : null,
      clock_out_time: initialClockOut
        ? dayjs(String(initialClockOut).slice(0, 5), 'HH:mm')
        : null,
      reason: '',
    });
  }, [open, initialClockIn, initialClockOut, form]);

  const handleSubmit = async () => {
    if (attendanceId == null) {
      message.error('شناسه تردد مشخص نیست');
      return;
    }
    try {
      const values = await form.validateFields();
      const clockIn = values.clock_in_time
        ? dayjs.isDayjs(values.clock_in_time)
          ? values.clock_in_time.format('HH:mm')
          : dayjs(values.clock_in_time).format('HH:mm')
        : null;
      const clockOut = values.clock_out_time
        ? dayjs.isDayjs(values.clock_out_time)
          ? values.clock_out_time.format('HH:mm')
          : dayjs(values.clock_out_time).format('HH:mm')
        : null;

      if (!clockIn && !clockOut) {
        message.error('حداقل یکی از ساعت ورود یا خروج را وارد کنید');
        return;
      }
      if (!values.reason?.trim()) {
        message.error('دلیل تعدیل الزامی است');
        return;
      }

      const payload = { reason: values.reason.trim() };
      if (clockIn) payload.clock_in_time = clockIn;
      if (clockOut) payload.clock_out_time = clockOut;

      setSaving(true);
      await attendanceApi.adjust(attendanceId, payload);
      message.success('تردد با موفقیت تعدیل شد');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else if (!error?.errorFields) {
        message.error(error?.message || 'خطا در تعدیل تردد');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
          <EditOutlined className="text-amber-500" />
          <span>تعدیل تردد</span>
        </div>
      }
      footer={null}
      centered
      width={480}
      destroyOnClose
    >
      <div style={{ fontFamily: 'CustomFont' }}>
        <div
          className="rounded-lg p-3 mb-3"
          style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}
        >
          <div className="font-semibold text-amber-900">{empName || '—'}</div>
          {jalaliDate ? <div className="text-xs text-amber-700 mt-0.5">{jalaliDate}</div> : null}
        </div>

        <Form form={form} layout="vertical">
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="clock_in_time"
              label={<span style={{ fontFamily: 'CustomFont' }}>ساعت ورود</span>}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="08:00" />
            </Form.Item>
            <Form.Item
              name="clock_out_time"
              label={<span style={{ fontFamily: 'CustomFont' }}>ساعت خروج</span>}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="17:00" />
            </Form.Item>
          </div>
          <Form.Item
            name="reason"
            label={<span style={{ fontFamily: 'CustomFont' }}>دلیل تعدیل</span>}
            rules={[{ required: true, message: 'دلیل تعدیل الزامی است' }]}
          >
            <TextArea
              rows={3}
              placeholder="دلیل را بنویسید"
              style={{ fontFamily: 'CustomFont' }}
            />
          </Form.Item>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <Button onClick={onClose} style={{ fontFamily: 'CustomFont' }}>
              انصراف
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSubmit}
              className="bg-amber-600 hover:bg-amber-700 border-amber-600"
              style={{ fontFamily: 'CustomFont' }}
            >
              ثبت تعدیل
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AttendanceAdjustModal;
